import { GameState, GameAction, GameEvent, CardDef, PlayerSide, AttackGroup } from '../types.js';
import { createBoardDOM, renderBoard, refreshGameLog } from './BoardView.js';
import { getPlayerState, getInstance, getEffectivePower, cloneState } from '../engine/GameState.js';
import { canPlayCharacter, canSupport, canActivateAbility, canPlayAction, canPlayReaction, wouldSupportIfPlayed } from '../engine/Rules.js';
import { getConfig } from '../config.js';
import { getMaxHandSize, hasPassive } from '../engine/AbilityProcessor.js';
import { createArrowOverlay, drawArrows, clearArrows } from './CombatArrows.js';
import { DragController } from './DragController.js';
import { TaskList } from './TaskList.js';
import { showCardDetail } from './CardDetailView.js';
import { hideTooltip } from './CardView.js';
import { ticketDisplayName } from '../shared/tickets.js';
import { showVoiceBubble, type VoiceBubbleAnchor } from './VoiceBubble.js';
import { pickVoiceLine, logVoiceLine } from './VoiceLinePool.js';
import { lookupDossierOrNull } from '../collection/DossierCache.js';
import type { VoiceTrigger } from '../shared/dossier-types.js';

// Returns void for fire-and-forget callers (drag-drop, ability
// buttons), or a Promise the caller can await when it needs to
// sequence multiple dispatches. The end-turn flow awaits between its
// two SKIP_PHASE steps so the turn-crossing banner (e.g. an
// end-of-turn damage trigger) fully plays — and the AI turn is
// scheduled — only after the first step settles.
export type ActionCallback = (action: GameAction) => void | Promise<void>;

// ── Tutorial System ──────────────────────────────────────────────

type TutorialKey = 'rally' | 'support' | 'attack' | 'block' | 'activate' | 'united' | 'endTurn' | 'pressAttack';

const TUTORIAL_STORAGE_KEY = 'cardverse-tutorials';

const TUTORIAL_MESSAGES: Record<TutorialKey, string> = {
  rally: 'Drag a character from your hand onto an empty ring slot to <strong>Play</strong> them into battle. You can play one character per turn.',
  support: 'Drag a character from your hand onto a ring character you\'ve already played to <strong>Support</strong> them. They need to share at least 2 tags. The hand card is consumed and the ring character gains 300 bonus Power.',
  attack: 'Drag a ready character from your ring forward to the center line to declare them as an <strong>Attacker</strong>. Reduce your opponent\'s HP to zero to win!',
  block: 'Drag one of your ring characters onto an opponent\'s attacker to <strong>Block</strong>. Blockers absorb damage instead of your HP.',
  activate: 'Some characters have <strong>activated abilities</strong>. Tap the Activate button on a character to use their ability. This will exhaust them, so they can\'t attack or block this turn.',
  united: 'Drag a second attacker onto an already-staged attacker to form a <strong>United Attack</strong>. United attackers combine their power and pierce for excess damage to HP.',
  endTurn: 'You have nothing more to do this turn. Press <strong>End Turn</strong> to let your opponent play.',
  pressAttack: 'Press <strong>Attack</strong> to send your characters into battle. Your turn will end after they attack.',
};

function loadSeenTutorials(): Set<TutorialKey> {
  try {
    const raw = localStorage.getItem(TUTORIAL_STORAGE_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch { return new Set(); }
}

function saveTutorialSeen(key: TutorialKey, seen: Set<TutorialKey>): void {
  seen.add(key);
  localStorage.setItem(TUTORIAL_STORAGE_KEY, JSON.stringify([...seen]));
}

interface CombatBeat {
  events: GameEvent[];
  involvedIds: Set<string>;
}

interface InteractionState {
  // Map of primary attacker instanceId → [primary, ...joiners]. Size ≥ 2 means united.
  stagedAttackers: Map<string, string[]>;
  // The hand-area card the cursor is currently over (null if none). Drives
  // the green support-target highlight on ring cards the hovered hand card
  // could support.
  hoveredHandId: string | null;
  // The ring card the cursor is over. Drives the green support-source
  // highlight on hand cards that could support the hovered ring character.
  hoveredRingId: string | null;
}

/** Shape passed to the Renderer constructor so the game-over modal can
 *  render the prize line without re-querying the rules bundle. The
 *  caller (main.ts:getRewardPreview) is responsible for branching on
 *  first-win vs replay-win vs skirmish before populating this. */
export interface RewardPreview {
  winCoins: number;
  winGems: number;
  lossCoins: number;
  lossGems: number;
  winTickets: { id: string; count: number }[];
  /** True if the win will yield any cards (from `cards` or `pack` on
   *  the relevant Reward). Drives the Claim Pack button's visibility:
   *  we suppress it when a card reward is incoming because the post-
   *  Continue pack-opening view will offer Add to Deck instead. */
  winHasCardReward: boolean;
  /** Set for tournament mode after the match report has resolved.
   *  Drives the rank-delta line in the game-over modal. The Renderer
   *  itself never populates this — main.ts wires it up via
   *  `setTournamentRankChange` once the server has reported the new
   *  rank. */
  tournamentRankChange?: TournamentRankChange | null;
}

export interface TournamentRankChange {
  oldRank: number;
  newRank: number;
  total: number;
  /** True if this match's result affected the player's priority.
   *  False when the tournament ended between match start and report —
   *  per-match reward is still granted but the rank wasn't moved by
   *  THIS match. (The current rank shown may still differ from
   *  oldRank because other players moved.) */
  rankUpdated: boolean;
  tournamentEnded: boolean;
}

/** Action the player took to dismiss the game-over modal. Continue
 *  takes them back along the path they came from (campaign-stage list
 *  or home for skirmish); Claim Pack always routes to the main menu so
 *  they can spend their freshly-earned ticket on the shop's FREE pack
 *  tile. */
export type GameOverAction = 'continue' | 'claim-pack';

export class Renderer {
  private board: HTMLElement;
  private state: GameState | null = null;
  private catalog: Map<string, CardDef>;
  private onAction: ActionCallback;
  private onGameOverCallback: ((winner: PlayerSide, action: GameOverAction) => void) | null;
  private rewardPreview: RewardPreview;
  private interaction: InteractionState;
  private seenTutorials: Set<TutorialKey>;
  private activeTutorial: TutorialKey | null = null;
  private tutorialEl: HTMLElement | null = null;
  private drag: DragController;
  private taskList: TaskList;
  // Set once `showGameOver` runs so subsequent render() calls (a single
  // dispatch can render multiple times — combat animation, then post-
  // combat phase advance, etc.) don't stack additional game-over
  // overlays on top of the first one.
  private gameOverShown = false;

  // When an ability kills the player (or opponent's last unit) we want
  // the effect banner + flyaway to play first; otherwise the game-over
  // modal pops over an empty board with no causal explanation of what
  // just killed you. Callers set this flag before render() during a
  // dispatch that emits game-over alongside banner-triggering events,
  // then call `flushGameOver()` after the banner sequence completes.
  private gameOverDeferred = false;

  // ── Voice-line state (Phase 2) ──────────────────────────────────
  // Profile-card identities, set by main.ts at match-start via
  // `setProfileIdentities`. Drives both the header-strip avatars
  // (rendered above) and the dossier lookup for opponent_surprised /
  // opponent_gloat bubbles fired from the upper-left icon.
  private opponentProfileCardId: string | null = null;
  private playerProfileCardId: string | null = null;
  // Per-character per-turn debounce. Cleared on every transition into
  // `activate` phase. Tracks instanceIds that have already spoken
  // this turn so a single character can't fire `played` then a
  // staging-time combat_engage then a resolution-time die / kill_other
  // all in the same turn — the same character speaks at most once
  // per turn.
  private charactersSpokeThisTurn = new Set<string>();
  // Last-known DOM rects for ring cards that are about to be removed.
  // Populated by render() RIGHT BEFORE the DOM rebuild, so when voice
  // routing fires `die` bubbles for non-combat deaths (action card
  // damage, ability KOs) after render has already removed the card
  // from the DOM, tryFireCardTrigger can still anchor the bubble at
  // the character's last visible position via this snapshot.
  private lastKnownRects = new Map<string, DOMRect>();

  constructor(
    container: HTMLElement,
    catalog: Map<string, CardDef>,
    onAction: ActionCallback,
    onGameOver: ((winner: PlayerSide, action: GameOverAction) => void) | undefined,
    rewardPreview: RewardPreview,
  ) {
    this.catalog = catalog;
    this.onAction = onAction;
    this.onGameOverCallback = onGameOver ?? null;
    this.rewardPreview = rewardPreview;
    this.seenTutorials = loadSeenTutorials();
    this.board = createBoardDOM();
    container.appendChild(this.board);
    this.interaction = this.freshInteraction();
    this.setupEventListeners();
    this.setupLogCardLinks();
    createArrowOverlay(this.board);
    this.taskList = new TaskList(this.board.querySelector('.task-list') as HTMLElement, this.catalog);
    this.drag = new DragController({
      board: this.board,
      getState: () => this.state,
      getCatalog: () => this.catalog,
      getStagedAttackers: () => this.interaction.stagedAttackers,
      getStagedBlockers: () => this.state?.combatState?.blockerAssignments ?? new Map(),
      stageAttacker: (id) => this.stageAttacker(id),
      unstageAttacker: (id) => this.unstageAttacker(id),
      joinAttacker: (primaryId, joinerId) => this.joinAttacker(primaryId, joinerId),
      unjoinAttacker: (id) => this.unjoinAttacker(id),
      unstageBlocker: (id) => this.onAction({ type: 'UNASSIGN_BLOCKER', blockerInstanceId: id }),
      onAction: (a) => this.onAction(a),
      refresh: () => this.refreshInteractionOnly(),
    });
  }

  private freshInteraction(): InteractionState {
    clearArrows();
    return { stagedAttackers: new Map(), hoveredHandId: null, hoveredRingId: null };
  }

  /** Replace the "Opponent" / "You" text labels in the header strip
   *  with the profile-card avatars for each side. Called by `main.ts`
   *  at match-start once the identities are resolved (campaign
   *  protagonist, tournament profile card, or skirmish-picked card).
   *  Passing null for either side restores the text fallback. */
  setProfileIdentities(opponentCardId: string | null, playerCardId: string | null): void {
    this.opponentProfileCardId = opponentCardId;
    this.playerProfileCardId = playerCardId;
    this.renderProfileLabel('opponent', opponentCardId);
    this.renderProfileLabel('player', playerCardId);
  }

  /** Read access for main.ts / interstitial — they read these in
   *  callbacks after the renderer is already mounted. */
  getOpponentProfileCardId(): string | null { return this.opponentProfileCardId; }
  getPlayerProfileCardId(): string | null { return this.playerProfileCardId; }

  /** Re-paint just the game-log DOM section. Used by callers (e.g.
   *  main.ts fireOpponentIntroBubble) that push log entries
   *  out-of-band — after render() has already run — and need the new
   *  entries to surface immediately. */
  refreshLog(): void {
    if (!this.state) return;
    refreshGameLog(this.board, this.state);
  }

  /** Look up the side's `.player-label` element and swap its contents
   *  for either an avatar (when a cardId + art are available) or the
   *  original uppercase text. Reuses the ladder's existing avatar
   *  classes for visual consistency. */
  private renderProfileLabel(side: PlayerSide, cardId: string | null): void {
    const label = this.board.querySelector(`.player-info.${side} .player-label`) as HTMLElement | null;
    if (!label) return;
    const def = cardId ? this.catalog.get(cardId) : null;
    if (!def || !def.art) {
      label.classList.remove('player-label-avatar');
      label.textContent = side === 'opponent' ? 'Opponent' : 'You';
      return;
    }
    label.classList.add('player-label-avatar');
    const ariaLabel = side === 'opponent' ? 'Opponent' : 'You';
    // Anchor for the opponent_intro / interstitial speech bubbles is
    // identified by `data-profile-side` so VoiceBubble can target it
    // by query selector without coupling to the avatar class names.
    label.innerHTML = `
      <span class="tournament-ladder-avatar player-profile-avatar"
            role="button" tabindex="0"
            data-card-id="${escapeHtml(cardId!)}"
            aria-label="${ariaLabel}"
            title="${escapeHtml(def.name)}">
        <img src="${escapeHtml(def.art)}" alt="${escapeHtml(def.name)}" draggable="false" />
      </span>
    `;
    const avatar = label.querySelector('.tournament-ladder-avatar') as HTMLElement | null;
    if (avatar) {
      const open = (ev: Event): void => {
        ev.stopPropagation();
        showCardDetail(cardId!, this.catalog);
      };
      avatar.addEventListener('click', open);
      avatar.addEventListener('keydown', (ev) => {
        if ((ev as KeyboardEvent).key === 'Enter' || (ev as KeyboardEvent).key === ' ') open(ev);
      });
    }
  }

  render(state: GameState): void {
    // Snapshot rects of ring cards that are about to disappear so
    // voice routing later in the dispatch can still anchor `die`
    // bubbles for non-combat deaths. The DOM rebuild below removes
    // the cardEls before showEventPopups runs.
    this.lastKnownRects.clear();
    if (this.state) {
      const newRingIds = new Set<string>();
      for (const ps of [state.player, state.opponent]) {
        for (const slot of ps.ring) {
          if (slot.instanceId) newRingIds.add(slot.instanceId);
        }
      }
      for (const ps of [this.state.player, this.state.opponent]) {
        for (const slot of ps.ring) {
          const id = slot.instanceId;
          if (!id || newRingIds.has(id)) continue;
          const cardEl = this.board.querySelector(
            `.card[data-instance-id="${id}"]`,
          ) as HTMLElement | null;
          if (cardEl) this.lastKnownRects.set(id, cardEl.getBoundingClientRect());
        }
      }
    }

    this.state = state;

    // Clear staged attackers whenever we're not in the player's main phase.
    if (!(state.phase === 'main' && state.currentPlayer === 'player')) {
      this.interaction.stagedAttackers.clear();
    } else {
      // Drop any staged attacker that became ineligible mid-stage — activating
      // an ability exhausts a character, KO'd characters leave the ring, etc.
      // Otherwise pressing Attack would declare an exhausted character and
      // they'd still fight despite the exhaustion (the engine's DECLARE_ATTACKERS
      // check was already bypassed at staging time).
      for (const [primaryId, members] of Array.from(this.interaction.stagedAttackers)) {
        const primary = getInstance(state, primaryId);
        if (primary.zone !== 'ring' || primary.isFresh || primary.isExhausted) {
          this.interaction.stagedAttackers.delete(primaryId);
          continue;
        }
        const kept = members.filter(id => {
          if (id === primaryId) return true;
          const inst = getInstance(state, id);
          return inst.zone === 'ring' && !inst.isFresh && !inst.isExhausted;
        });
        if (kept.length !== members.length) {
          this.interaction.stagedAttackers.set(primaryId, kept);
        }
      }
    }

    renderBoard(this.board, state, this.catalog, {
      stagedAttackers: this.interaction.stagedAttackers,
      maxHandSize: getMaxHandSize(state, 'player', this.catalog),
    });
    this.updateInteractionUI();
    this.updateActionButtons();
    this.updateCombatUI();
    this.applyHoverHighlight();
    this.taskList.syncTurn(state, this.interaction.stagedAttackers);
    this.taskList.renderInto(state);
    this.checkTutorial(state);

    if (state.winner && !this.gameOverShown && !this.gameOverDeferred) {
      this.gameOverShown = true;
      this.showGameOver(state);
    }
  }

  /** Suppress the game-over modal on the next render. Used by the
   *  dispatch pipeline when an effect both kills a player and triggers a
   *  banner — the banner must play before the modal so the player sees
   *  what killed them. Paired with `flushGameOver()`. */
  deferGameOver(): void {
    this.gameOverDeferred = true;
  }

  /** Update the tournament rank-change shown in the game-over modal.
   *  Called by main.ts after the tournament match report resolves so
   *  the rank line on the modal reflects the authoritative server
   *  state. Must be called BEFORE `flushGameOver` for tournament
   *  matches; the modal reads `rewardPreview` at render time and
   *  doesn't re-render once shown. */
  setTournamentRankChange(change: TournamentRankChange): void {
    this.rewardPreview.tournamentRankChange = change;
  }

  /** Clear the deferral and show the modal now if a winner exists. Idempotent;
   *  multiple flushes (or a flush with no winner) are no-ops. */
  flushGameOver(): void {
    this.gameOverDeferred = false;
    if (this.state?.winner && !this.gameOverShown) {
      this.gameOverShown = true;
      this.showGameOver(this.state);
    }
  }

  onActionCommitted(action: GameAction): void {
    this.taskList.onActionCommitted(action);
  }

  // Re-renders only the interaction overlay (staged classes, drop targets,
  // action buttons). Used after local staging changes that don't dispatch an
  // engine action.
  private refreshInteractionOnly(): void {
    if (!this.state) return;
    renderBoard(this.board, this.state, this.catalog, {
      stagedAttackers: this.interaction.stagedAttackers,
      maxHandSize: getMaxHandSize(this.state, 'player', this.catalog),
    });
    this.updateInteractionUI();
    this.updateActionButtons();
    this.updateCombatUI();
    this.applyHoverHighlight();
    this.taskList.syncTurn(this.state, this.interaction.stagedAttackers);
    this.taskList.renderInto(this.state);
    // Tutorials whose triggers depend on staging (united, pressAttack,
    // endTurn) need to re-evaluate after a stage/unstage even though the
    // engine state didn't change.
    this.checkTutorial(this.state);
  }

  /** Delegated click handler on the game-log entries. Each entry's HTML
   *  may contain `<span class="log-card-link" data-card-id="...">` tokens
   *  built from `{{card:id|name}}` markers in `state.log` — clicking one
   *  opens the read-only card detail modal. Bound once at construction
   *  rather than per-render so the listener doesn't compound. */
  private setupLogCardLinks(): void {
    const logEntries = this.board.querySelector('.log-entries') as HTMLElement;
    logEntries.addEventListener('click', (e) => {
      const link = (e.target as HTMLElement).closest('.log-card-link') as HTMLElement | null;
      if (!link) return;
      const cardId = link.dataset.cardId;
      if (!cardId) return;
      e.stopPropagation();
      showCardDetail(cardId, this.catalog);
    });
  }

  private setupEventListeners(): void {
    // Main action button (phase advance / attack / discard)
    const mainBtn = this.board.querySelector('#main-action-btn') as HTMLButtonElement;
    mainBtn.addEventListener('click', () => this.handleMainAction());

    // Secondary action button (currently unused — retained for layout stability)
    const secBtn = this.board.querySelector('#secondary-action-btn') as HTMLButtonElement;
    secBtn.addEventListener('click', () => this.handleSecondaryAction());

    // Hover on hand/ring cards → show green support pairing. Hand card hover
    // highlights ring cards it can support; ring card hover highlights hand
    // cards that can support it. Uses pointerover/out so it bubbles through
    // delegation and survives card re-renders. While a drag is active the
    // pointer is captured on the source card and these events won't fire on
    // siblings, so drag-and-hover don't conflict.
    this.board.addEventListener('pointerover', (e) => {
      const handCard = (e.target as HTMLElement).closest(
        '.hand-area[data-side="player"] .card[data-instance-id]',
      ) as HTMLElement | null;
      if (handCard) {
        const id = handCard.dataset.instanceId!;
        if (this.interaction.hoveredHandId !== id) {
          this.interaction.hoveredHandId = id;
          this.applyHoverHighlight();
        }
        return;
      }
      const ringCard = (e.target as HTMLElement).closest(
        '.ring-area[data-side="player"] .card[data-instance-id]',
      ) as HTMLElement | null;
      if (ringCard) {
        const id = ringCard.dataset.instanceId!;
        if (this.interaction.hoveredRingId !== id) {
          this.interaction.hoveredRingId = id;
          this.applyHoverHighlight();
        }
      }
    });
    this.board.addEventListener('pointerout', (e) => {
      const related = (e as PointerEvent).relatedTarget as Node | null;

      const handCard = (e.target as HTMLElement).closest(
        '.hand-area[data-side="player"] .card[data-instance-id]',
      ) as HTMLElement | null;
      if (handCard) {
        if (related && handCard.contains(related)) return;
        if (this.interaction.hoveredHandId === handCard.dataset.instanceId) {
          this.interaction.hoveredHandId = null;
          this.clearHoverHighlight();
        }
        return;
      }
      const ringCard = (e.target as HTMLElement).closest(
        '.ring-area[data-side="player"] .card[data-instance-id]',
      ) as HTMLElement | null;
      if (ringCard) {
        if (related && ringCard.contains(related)) return;
        if (this.interaction.hoveredRingId === ringCard.dataset.instanceId) {
          this.interaction.hoveredRingId = null;
          this.clearHoverHighlight();
        }
      }
    });
  }

  private applyHoverHighlight(): void {
    if (!this.state) return;

    const inMainPhase = this.state.phase === 'main' && this.state.currentPlayer === 'player';
    const ps = getPlayerState(this.state, 'player');

    // Validate stored hover ids against current state; if the hovered
    // card is gone, clear our memory of it. We still want to walk both
    // sets of nodes to toggle classes off — see below.
    let activeHandId = this.interaction.hoveredHandId;
    if (activeHandId && !ps.hand.includes(activeHandId)) {
      this.interaction.hoveredHandId = null;
      activeHandId = null;
    }
    let activeRingId = this.interaction.hoveredRingId;
    if (activeRingId && !ps.ring.some(s => s.instanceId === activeRingId)) {
      this.interaction.hoveredRingId = null;
      activeRingId = null;
    }
    if (!inMainPhase) {
      activeHandId = null;
      activeRingId = null;
    }

    // Toggle .support-target on every player-ring card. With keyed
    // reconciliation, cards persist across renders — a toggle that
    // matches the current class state is a no-op, so transitions only
    // fire on actual flips. The walk also doubles as a "clear" pass
    // for cards that previously matched but no longer do.
    //
    // Two-strength glow: cards that ARE supportable right now use the
    // strong .support-target class; cards that would be supportable
    // except the player has spent their supports use the faint
    // .support-target-faint variant. The faint glow lets the player
    // still see tag-compatibility relationships post-support without
    // implying they can act on them.
    this.board
      .querySelectorAll<HTMLElement>('.ring-area[data-side="player"] .card[data-instance-id]')
      .forEach(el => {
        const ringId = el.dataset.instanceId!;
        const active = activeHandId !== null
          && canSupport(this.state!, activeHandId, ringId, this.catalog);
        const faint = !active && activeHandId !== null
          && canSupport(this.state!, activeHandId, ringId, this.catalog, { ignoreSupportsUsed: true });
        el.classList.toggle('support-target', active);
        el.classList.toggle('support-target-faint', faint);
      });

    // Symmetric pass for .support-source on hand cards. Two trigger
    // paths feed this:
    //   - Ring-card hover: the canonical "this hand card can support the
    //     hovered ring character" highlight — strong glow when supports
    //     are available, faint when they're spent.
    //   - Hand-card hover: predictive "if you played the hovered card,
    //     this other hand card could support it next" — uses
    //     wouldSupportIfPlayed which mirrors canSupport's tag-match
    //     logic but skips the ring-zone gating since the hovered card
    //     isn't on the ring yet. ALWAYS rendered faint regardless of
    //     supports-used, because you can't combine the two cards
    //     immediately — the hovered card has to land on the ring first
    //     — so the bright glow would be misleading.
    this.board
      .querySelectorAll<HTMLElement>('.hand-area[data-side="player"] .card[data-instance-id]')
      .forEach(el => {
        const id = el.dataset.instanceId!;
        // Strong only for ring-hover with supports available.
        const active = activeRingId !== null
          && canSupport(this.state!, id, activeRingId, this.catalog);
        const faintFromRing = !active && activeRingId !== null
          && canSupport(this.state!, id, activeRingId, this.catalog, { ignoreSupportsUsed: true });
        const faintFromHand = !active && activeHandId !== null
          && wouldSupportIfPlayed(this.state!, id, activeHandId, this.catalog, { ignoreSupportsUsed: true });
        const faint = faintFromRing || faintFromHand;
        el.classList.toggle('support-source', active);
        el.classList.toggle('support-source-faint', faint);
      });
  }

  private clearHoverHighlight(): void {
    // Remove BOTH the strong and faint variants. Missing the faint
    // classes here was a real bug: the faint glow would stick around
    // after pointerout (visible as a ghost green border), and because
    // `.support-source-faint` is more specific than `.playable`, it
    // also masked the playable highlight on hand cards until the next
    // re-render that recomputed playability.
    this.board
      .querySelectorAll('.ring-area[data-side="player"] .card.support-target, .ring-area[data-side="player"] .card.support-target-faint')
      .forEach(el => el.classList.remove('support-target', 'support-target-faint'));
    this.board
      .querySelectorAll('.hand-area[data-side="player"] .card.support-source, .hand-area[data-side="player"] .card.support-source-faint')
      .forEach(el => el.classList.remove('support-source', 'support-source-faint'));
  }

  // ── Attacker staging helpers (local UI state) ─────────────────────

  private stageAttacker(primaryId: string): void {
    this.interaction.stagedAttackers.set(primaryId, [primaryId]);
  }

  private unstageAttacker(primaryId: string): void {
    this.interaction.stagedAttackers.delete(primaryId);
  }

  private joinAttacker(primaryId: string, joinerId: string): void {
    const group = this.interaction.stagedAttackers.get(primaryId);
    if (!group || group.length >= 3) return;
    if (group.includes(joinerId)) return;
    group.push(joinerId);
  }

  private unjoinAttacker(joinerId: string): void {
    for (const [, members] of this.interaction.stagedAttackers) {
      const idx = members.indexOf(joinerId);
      if (idx > 0) {
        members.splice(idx, 1);
        return;
      }
    }
  }

  private buildStagedGroups(): AttackGroup[] {
    const groups: AttackGroup[] = [];
    for (const [, members] of this.interaction.stagedAttackers) {
      if (members.length >= 2) {
        groups.push({ attackerIds: [...members], united: true });
      } else {
        groups.push({ attackerIds: [...members], united: false });
      }
    }
    return groups;
  }

  // ── Phase-button state machine ────────────────────────────────────

  private async handleMainAction(): Promise<void> {
    if (!this.state) return;
    const phase = this.state.phase;
    const isPlayerTurn = this.state.currentPlayer === 'player';

    if (phase === 'main' && isPlayerTurn) {
      const groups = this.buildStagedGroups();
      if (groups.length === 0) {
        // "End Turn" — advance main→attack, then attack→end/cleanup (SKIP_PHASE
        // in attack auto-advances through end since combatState is null).
        // AWAIT the first SKIP before the second: otherwise the two
        // dispatches run concurrently, the turn-crossing banner (e.g.
        // an end-of-turn damage trigger) plays interleaved with the
        // first step, and the AI turn gets scheduled off whichever step
        // settles first — landing the opponent's opening move on top of
        // the still-playing banner.
        await this.onAction({ type: 'SKIP_PHASE' });
        if (this.state?.phase === 'attack') {
          await this.onAction({ type: 'SKIP_PHASE' });
        }
      } else {
        // "Attack" — advance main→attack, then declare groups.
        await this.onAction({ type: 'SKIP_PHASE' });
        if (this.state?.phase === 'attack') {
          await this.onAction({ type: 'DECLARE_ATTACKERS', groups });
        }
      }
      this.interaction.stagedAttackers.clear();
    } else if (phase === 'counter' && !isPlayerTurn) {
      await this.onAction({ type: 'CONFIRM_BLOCKERS' });
    }
  }

  private handleSecondaryAction(): void {
    // Reserved for future use.
  }

  // ── UI updates ────────────────────────────────────────────────────

  private updateInteractionUI(): void {
    if (!this.state) return;

    const isMainPhase = this.state.phase === 'main' && this.state.currentPlayer === 'player';
    const isDefending = this.state.phase === 'counter' && this.state.currentPlayer !== 'player';
    const isCleanupDiscard = this.state.phase === 'cleanup' && this.state.currentPlayer === 'player';

    const playerPs = getPlayerState(this.state, 'player');

    // Hand cards — toggle .playable and .selectable per-card based on
    // legality. With keyed reconciliation, the card DOM nodes survive
    // across renders, so `classList.toggle(name, bool)` is a no-op when
    // the class is already in the desired state — no spurious CSS
    // transition fires on the border / box-shadow each render.
    const handCards = this.board.querySelectorAll('.hand-area.player .card');
    handCards.forEach(card => {
      const htmlCard = card as HTMLElement;
      const handId = htmlCard.dataset.instanceId;
      if (!handId) return;
      const inst = getInstance(this.state!, handId);
      const def = this.catalog.get(inst.cardId);
      if (!def) return;

      let playable = false;
      if (isMainPhase) {
        if (def.type === 'character') {
          const canRally = playerPs.ring.some((_, s) =>
            canPlayCharacter(this.state!, handId, s, this.catalog),
          );
          const canSupportAny = playerPs.ring.some(slot =>
            slot.instanceId !== null && canSupport(this.state!, handId, slot.instanceId!, this.catalog),
          );
          playable = canRally || canSupportAny;
        } else if (def.type === 'action') {
          playable = canPlayAction(this.state!, handId, this.catalog);
        }
      } else if (isDefending && def.type === 'action' && def.subtype === 'reaction') {
        playable = canPlayReaction(this.state!, handId, this.catalog);
      }

      htmlCard.classList.toggle('playable', playable);
      htmlCard.classList.toggle('selectable', isCleanupDiscard);
    });

    // Remove old activate buttons — they're rebuilt per-render below
    // because their click handler closes over the current instance id
    // and would be stale on a reused DOM node.
    this.board.querySelectorAll('.activate-btn').forEach(b => b.remove());

    // Ring cards: activate buttons, .playable, .blocker-candidate.
    const playerRingCards = this.board.querySelectorAll('.ring-area[data-side="player"] .card');
    playerRingCards.forEach(card => {
      const htmlCard = card as HTMLElement;
      const iId = htmlCard.dataset.instanceId;
      if (!iId) {
        htmlCard.classList.remove('playable', 'blocker-candidate');
        return;
      }

      if (isMainPhase && canActivateAbility(this.state!, iId, this.catalog)) {
        const btn = document.createElement('button');
        btn.className = 'activate-btn';
        btn.textContent = 'Activate';
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          // Touch on the button triggers the card's pointerenter first, which
          // pops the tooltip and would otherwise hover above the effect banner
          // and animations that fire when the ability resolves.
          hideTooltip();
          this.onAction({ type: 'ACTIVATE_ABILITY', instanceId: iId });
        });
        htmlCard.appendChild(btn);
      }

      let playable = false;
      if (isMainPhase) {
        const inst = getInstance(this.state!, iId);
        playable = !inst.isFresh && !inst.isExhausted && !this.isAnyStaged(iId);
      }
      htmlCard.classList.toggle('playable', playable);

      let blockerCandidate = false;
      if (isDefending) {
        const inst = getInstance(this.state!, iId);
        const combatAssigned = this.state!.combatState?.blockerAssignments;
        const isAssigned = combatAssigned && Array.from(combatAssigned.values()).includes(iId);
        const cannotBlock = hasPassive(this.state!, iId, 'passive-cannot-block', this.catalog);
        blockerCandidate = !inst.isExhausted && !isAssigned && !cannotBlock;
      }
      htmlCard.classList.toggle('blocker-candidate', blockerCandidate);
    });
  }

  private isAnyStaged(instanceId: string): boolean {
    for (const [, members] of this.interaction.stagedAttackers) {
      if (members.includes(instanceId)) return true;
    }
    return false;
  }

  private updateActionButtons(): void {
    if (!this.state) return;

    const mainBtn = this.board.querySelector('#main-action-btn') as HTMLButtonElement;
    const secBtn = this.board.querySelector('#secondary-action-btn') as HTMLButtonElement;
    const phase = this.state.phase;
    const isPlayerTurn = this.state.currentPlayer === 'player';

    mainBtn.style.display = 'none';
    secBtn.style.display = 'none';

    if (this.state.winner) return;

    if (isPlayerTurn && (phase === 'main' || phase === 'cleanup')) {
      const ps = getPlayerState(this.state, 'player');
      const maxHand = getMaxHandSize(this.state, 'player', this.catalog);
      const excess = ps.hand.length - maxHand;

      if (excess > 0) {
        mainBtn.style.display = '';
        mainBtn.textContent = `Discard ${excess} card${excess > 1 ? 's' : ''}`;
        mainBtn.disabled = true;
        return;
      }

      if (phase === 'main') {
        mainBtn.style.display = '';
        const groups = this.buildStagedGroups();
        if (groups.length === 0) {
          mainBtn.textContent = 'End Turn';
          mainBtn.disabled = false;
        } else {
          const config = getConfig();
          const united2 = groups.find(g => g.united && g.attackerIds.length === 2);
          const united3 = groups.find(g => g.united && g.attackerIds.length === 3);
          let cost = 0;
          let label = 'Attack';
          if (united3) { cost = config.unitedAttackCost3; label = `United Attack (−${cost} SP)`; }
          else if (united2) { cost = config.unitedAttackCost2; label = `United Attack (−${cost} SP)`; }
          mainBtn.textContent = label;
          mainBtn.disabled = cost > 0 && ps.spirit < cost;
        }
      }
      // phase === 'cleanup' with no excess: auto-advance handles it.
    } else if (phase === 'counter' && !isPlayerTurn) {
      mainBtn.style.display = '';
      mainBtn.textContent = 'Confirm Blocks';
      mainBtn.disabled = false;
    }
  }

  private updateCombatUI(): void {
    if (!this.state) return;

    this.board.querySelectorAll('.unblocked-badge').forEach(b => b.remove());

    const combat = this.state.combatState;
    if (!combat || this.state.phase !== 'counter') {
      clearArrows();
      return;
    }

    const arrowMap = new Map<string, string>();
    for (const [gIdx, blockerId] of combat.blockerAssignments) {
      const group = combat.groups[gIdx];
      if (!group) continue;
      for (const attackerId of group.attackerIds) {
        arrowMap.set(attackerId, blockerId);
      }
    }
    drawArrows(arrowMap, this.board);

    for (let gIdx = 0; gIdx < combat.groups.length; gIdx++) {
      if (combat.blockerAssignments.has(gIdx)) continue;
      for (const attackerId of combat.groups[gIdx].attackerIds) {
        const cardEl = this.board.querySelector(`.card[data-instance-id="${attackerId}"]`) as HTMLElement | null;
        if (!cardEl) continue;
        const parent = cardEl.closest('.ring-slot') as HTMLElement | null;
        if (parent) parent.style.position = 'relative';
        const badge = document.createElement('div');
        badge.className = 'unblocked-badge';
        badge.textContent = 'Unblocked';
        cardEl.appendChild(badge);
      }
    }
  }

  showCombatPreview(state: GameState): void {
    this.state = state;
    renderBoard(this.board, state, this.catalog, {
      stagedAttackers: this.interaction.stagedAttackers,
      maxHandSize: getMaxHandSize(state, 'player', this.catalog),
    });
    this.updateCombatUI();
  }

  async animateCombatResolution(events: GameEvent[], resolvedState: GameState): Promise<void> {
    const delay = getConfig().combatAnimationDelay;

    clearArrows();
    this.board.querySelectorAll('.unblocked-badge').forEach(b => b.remove());

    const beats = this.groupCombatBeats(events);
    const damageTaken = new Map<string, number>();
    // Mid-playback state. Starts as a clone of the pre-combat board and
    // evolves as each beat's KO events fire (we set their ring slots
    // to null). The displayPower computation in playCombatBeat reads
    // through this state instead of this.state so passives lift the
    // moment their provider KOs in the animation — e.g. an enemy
    // intimidator dying in beat 1 stops contributing its -100 to its
    // targets, and beat 2's blocker shows its true effective power
    // before damage starts coming in.
    const playback = this.state ? cloneState(this.state) : resolvedState;

    for (const beat of beats) {
      await this.playCombatBeat(beat, damageTaken, playback);
      // Evolve playback by removing each KO'd character from the rings
      // so subsequent beats compute effective power without their
      // passives. Damage already lives in `damageTaken`; the
      // displayPower formula factors it in.
      for (const ev of beat.events) {
        if (ev.type === 'character-ko') {
          for (const ps of [playback.player, playback.opponent]) {
            for (const slot of ps.ring) {
              if (slot.instanceId === ev.instanceId) slot.instanceId = null;
            }
          }
        }
      }
      // Refresh remaining ring power displays so passive-removal
      // visuals land at end of the killing beat, not at end of
      // combat. Without this a character whose intimidator just
      // died still reads their old (lower) power until the next
      // damage event touches them.
      this.refreshRingPowerDisplays(playback, damageTaken);
      await this.wait(delay);
    }
  }

  /** Walk both rings and update every live character's power-badge to
   *  reflect their current effective power minus animation-accumulated
   *  damage. Called at the end of each combat beat so passive changes
   *  (a provider dying) show up immediately instead of jumping at the
   *  end of combat. KO'd characters are skipped — they fade out via
   *  the .anim-ko class. */
  private refreshRingPowerDisplays(state: GameState, damageTaken: Map<string, number>): void {
    for (const ps of [state.player, state.opponent]) {
      for (const slot of ps.ring) {
        if (!slot.instanceId) continue;
        const cardEl = this.board.querySelector(`.card[data-instance-id="${slot.instanceId}"]`) as HTMLElement | null;
        if (!cardEl) continue;
        const powerEl = cardEl.querySelector('.card-power-badge');
        if (!powerEl) continue;
        const inst = getInstance(state, slot.instanceId);
        const totalDmg = damageTaken.get(slot.instanceId) ?? 0;
        const armorAbsorb = Math.min(inst.armor, totalDmg);
        const powerDmg = totalDmg - armorAbsorb;
        const basePower = getEffectivePower(state, slot.instanceId, this.catalog);
        const displayPower = basePower - powerDmg;
        powerEl.textContent = String(Math.max(0, displayPower));
      }
    }
  }

  private groupCombatBeats(events: GameEvent[]): CombatBeat[] {
    const beats: CombatBeat[] = [];
    let current: CombatBeat | null = null;

    for (const event of events) {
      if (event.type === 'combat-damage') {
        if (!current || (!current.involvedIds.has(event.attackerInstanceId) && !current.involvedIds.has(event.defenderInstanceId ?? ''))) {
          current = { events: [], involvedIds: new Set() };
          beats.push(current);
        }
        current.events.push(event);
        current.involvedIds.add(event.attackerInstanceId);
        if (event.defenderInstanceId) current.involvedIds.add(event.defenderInstanceId);
      } else if (event.type === 'hp-changed' || event.type === 'character-ko') {
        if (current) {
          current.events.push(event);
        }
      }
    }

    return beats;
  }

  private async playCombatBeat(beat: CombatBeat, damageTaken: Map<string, number>, playback: GameState): Promise<void> {
    const damageEvents = beat.events.filter(
      (e): e is GameEvent & { type: 'combat-damage' } => e.type === 'combat-damage',
    );
    const hpEvents = beat.events.filter(
      (e): e is GameEvent & { type: 'hp-changed' } => e.type === 'hp-changed',
    );
    const koEvents = beat.events.filter(
      (e): e is GameEvent & { type: 'character-ko' } => e.type === 'character-ko',
    );

    for (const dmg of damageEvents) {
      const attackerEl = this.board.querySelector(`.card[data-instance-id="${dmg.attackerInstanceId}"]`) as HTMLElement | null;
      if (attackerEl) {
        attackerEl.classList.add('anim-attack');
        attackerEl.addEventListener('animationend', () => attackerEl.classList.remove('anim-attack'), { once: true });
      }
    }

    await this.wait(200);

    for (const dmg of damageEvents) {
      if (dmg.defenderInstanceId) {
        const targetEl = this.board.querySelector(`.card[data-instance-id="${dmg.defenderInstanceId}"]`) as HTMLElement | null;
        if (targetEl) {
          targetEl.classList.add('anim-hit');
          targetEl.addEventListener('animationend', () => targetEl.classList.remove('anim-hit'), { once: true });
          this.showDamagePopup(targetEl, dmg.damage, 'damage');

          const prevDmg = damageTaken.get(dmg.defenderInstanceId) ?? 0;
          damageTaken.set(dmg.defenderInstanceId, prevDmg + dmg.damage);

          {
            // Read through the mid-playback state, not this.state.
            // this.state is the pre-combat snapshot from the last
            // render; using it here would compute basePower with
            // passives that have already died earlier in the
            // animation, undercounting effective power for any
            // character whose enemy intimidator KO'd in a prior beat.
            const inst = getInstance(playback, dmg.defenderInstanceId);
            const totalDmg = damageTaken.get(dmg.defenderInstanceId)!;

            const armorAbsorb = Math.min(inst.armor, totalDmg);
            const remainingArmor = inst.armor - armorAbsorb;
            const powerDmg = totalDmg - armorAbsorb;
            const basePower = getEffectivePower(playback, dmg.defenderInstanceId, this.catalog);
            const displayPower = basePower - powerDmg;

            const powerEl = targetEl.querySelector('.card-power-badge');
            if (powerEl) {
              powerEl.textContent = String(Math.max(0, displayPower));
            }

            const armorEl = targetEl.querySelector('.card-armor');
            if (remainingArmor > 0) {
              if (armorEl) {
                armorEl.textContent = `🛡${remainingArmor}`;
              }
            } else if (armorEl) {
              armorEl.remove();
            }
          }
        }
      }
    }

    const maxHp = this.state?.maxHp ?? 0;
    for (const hp of hpEvents) {
      const side = hp.player;
      const hpContainer = this.board.querySelector(`.player-info[data-side="${side}"] .hp-bar-container`) as HTMLElement | null;
      if (hpContainer) {
        // Sign of hp.amount distinguishes the source: positive = heal (e.g.
        // passive-leech on the attacker's side), negative = damage. Without
        // this, leech popups would float as -N red over the attacker's HP
        // bar instead of +N green.
        this.showDamagePopup(hpContainer, Math.abs(hp.amount), hp.amount > 0 ? 'heal' : 'damage');
        const displayHp = Math.max(0, hp.newHp);
        const hpBar = hpContainer.querySelector('.hp-bar') as HTMLElement;
        if (hpBar) {
          const hpPct = maxHp > 0 ? Math.max(0, (displayHp / maxHp) * 100) : 0;
          hpBar.style.width = `${hpPct}%`;
          hpBar.classList.toggle('low', hpPct < 30);
        }
        const hpText = this.board.querySelector(`.player-info[data-side="${side}"] .hp-text`);
        if (hpText) {
          hpText.textContent = `${displayHp} / ${maxHp}`;
        }
        hpContainer.classList.add('anim-damage');
        hpContainer.addEventListener('animationend', () => hpContainer.classList.remove('anim-damage'), { once: true });
      }
    }

    await this.wait(300);

    for (const ko of koEvents) {
      const koEl = this.board.querySelector(`.card[data-instance-id="${ko.instanceId}"]`) as HTMLElement | null;
      if (koEl) {
        koEl.classList.add('anim-ko');
      }
    }

    if (koEvents.length > 0) {
      await this.wait(500);
    }
  }

  private wait(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // ── Voice-line trigger routing ─────────────────────────────────
  // See `Combat sequencing` + `Throttling defaults` in the plan
  // (~/.claude/plans/sleepy-leaping-lynx.md). Called from
  // showEventPopups for standard dispatches, and from main.ts
  // directly for combat dispatches (which go through
  // animateCombatResolution and never reach showEventPopups).

  /** Public entry point — routes engine events to voice-line bubbles.
   *  Idempotent within a single event batch; safe to call alongside
   *  showEventPopups so long as the same events aren't passed in
   *  more than once (per-character debounce + per-turn pair rate
   *  limit guard duplicate fires within a batch anyway).
   *
   *  `stateOverride` is for paths where `this.state` is stale relative
   *  to the events being routed — specifically the CONFIRM_BLOCKERS
   *  combat path, which mutates main.ts's state ref before calling
   *  routeVoiceLines but doesn't run render() (which is what would
   *  normally re-sync this.state) until AFTER the combat animation
   *  finishes. Passing the override ensures voice-line log entries
   *  land in the same log array main.ts holds. */
  routeVoiceLines(events: GameEvent[], stateOverride?: GameState): void {
    const state = stateOverride ?? this.state;
    if (!state) return;
    this.processVoiceLineTriggers(events, state);
  }

  private processVoiceLineTriggers(events: GameEvent[], state: GameState): void {

    // Reset per-turn flags on transition into activate. A single batch
    // can include a phase-changed event for the new turn followed by
    // the activate-phase events; the reset must happen first so any
    // surprised/gloat fires don't see stale flags.
    for (const ev of events) {
      if (ev.type === 'phase-changed' && ev.phase === 'activate') {
        this.charactersSpokeThisTurn.clear();
      }
    }

    // played: fires for both sides' characters. Each play is a fresh
    // instanceId — debounce on that id keeps the character silent if it
    // somehow plays + acts in the same turn.
    //
    // Stash the ring-slot rect as a fallback anchor BEFORE calling
    // tryFireCardTrigger. Handles the self-destruct-on-play case
    // (e.g. "destroy this character and largest opponent character"):
    // the card transitions hand → ring → discard in a single dispatch,
    // so by the time voice routing runs, render() has the card in
    // discard and neither `.card[data-instance-id]` (DOM lookup) nor
    // the existing lastKnownRects capture (which scans the previous
    // state's ring) has an anchor for it. The ring slot itself stays
    // in the DOM regardless of whether a card is sitting in it, so
    // it's a stable position both `played` and `die` bubbles can use.
    for (const ev of events) {
      if (ev.type !== 'card-played') continue;
      this.captureRingSlotFallbackRect(ev.instanceId, ev.player, ev.slotIndex);
      this.tryFireCardTrigger(state, ev.instanceId, 'played', 0);
    }

    // ── Combat staging: one attacker speaks combat_engage ──────────
    // Fires at attack-declared time — the moment the player presses
    // Attack (their own attack) or the opponent commits to an attack
    // (the player's "Assign Blockers" UI is about to open). Picks the
    // lead attacker of the first declared group and fires one
    // combat_engage line. Defenders aren't eligible here because
    // blockers haven't been assigned yet.
    //
    // The bubble travels with the card automatically — VoiceBubble
    // re-snapshots the card's rect each animation frame, so the
    // staging lean (.attacker translate) and the subsequent return
    // to the ring slot both carry the bubble along.
    for (const ev of events) {
      if (ev.type !== 'attack-declared') continue;
      const firstGroup = ev.groups[0];
      if (!firstGroup || firstGroup.attackerIds.length === 0) continue;
      const isUnited = !!firstGroup.united && firstGroup.attackerIds.length >= 2;
      const prob = isUnited
        ? getConfig().voiceLineUnitedCombatProb
        : getConfig().voiceLineCombatProb;
      if (Math.random() >= prob) continue;
      this.tryFireCardTrigger(
        state,
        firstGroup.attackerIds[0],
        'combat_engage',
        0,
        /* bypassGate */ true,
      );
    }

    // ── Combat resolution: ONE bubble across the combat pairs ─────
    // Eligibility: attacker die / kill_other (no combat_engage —
    // that fired at staging), defender die / kill_other /
    // combat_engage. Gated by voiceLineCombatResolveProb.
    //
    // Suppressed when `attack-declared` is in the batch: that marks
    // a DECLARE_ATTACKERS dispatch, which can carry combat-damage
    // events from on-attack ability triggers (the `damage` verb
    // emits combat-damage too). Real combat resolution runs in
    // CONFIRM_BLOCKERS, which never emits attack-declared — so
    // routing combat-resolution lines off ability damage here was
    // mistaking AOE-style on-attack kills for combat pairs and
    // firing extra bubbles on top of the non-combat-death pick.
    const isDeclareDispatch = events.some(ev => ev.type === 'attack-declared');
    if (!isDeclareDispatch) {
      const combatPairEligibles = collectCombatResolutionEligibles(state, events);
      if (combatPairEligibles.length > 0) {
        if (Math.random() < getConfig().voiceLineCombatResolveProb) {
          const fireable = combatPairEligibles.filter(
            e => !this.charactersSpokeThisTurn.has(e.instanceId),
          );
          if (fireable.length > 0) {
            const pick = fireable[Math.floor(Math.random() * fireable.length)];
            this.tryFireCardTrigger(state, pick.instanceId, pick.trigger, 0, true);
          }
        }
      }
    }

    // ── Non-combat death: ONE bubble across cascade / ability /
    //    action-card kills ───────────────────────────────────────
    // A `character-ko` event whose victim does NOT have a matching
    // `kill-credit` was killed outside of direct combat:
    //   * Cascade KO during combat (passive provider dies and a
    //     buffed ally drops to 0). Rides in the same combat batch
    //     as combat events.
    //   * Action-card kills (Apocalypse, Tactical Strike, etc.)
    //   * Activated / triggered / passive ability damage.
    //
    // Gated by voiceLineNonCombatDeathProb — independent from the
    // combat resolution roll above, so both can fire in the same
    // dispatch (e.g. a clean kill + a cascade). Per-character
    // debounce prevents the same character from speaking twice.
    const credited = new Set<string>();
    for (const ev of events) {
      if (ev.type === 'kill-credit') credited.add(ev.victimInstanceId);
    }
    const nonCombatDeaths: string[] = [];
    for (const ev of events) {
      if (ev.type === 'character-ko' && !credited.has(ev.instanceId)) {
        nonCombatDeaths.push(ev.instanceId);
      }
    }
    if (nonCombatDeaths.length > 0) {
      if (Math.random() < getConfig().voiceLineNonCombatDeathProb) {
        const fireable = nonCombatDeaths.filter(
          id => !this.charactersSpokeThisTurn.has(id),
        );
        if (fireable.length > 0) {
          const pick = fireable[Math.floor(Math.random() * fireable.length)];
          this.tryFireCardTrigger(state, pick, 'die', 0, true);
        }
      }
    }

    // opponent-surprised / opponent-gloat: bubble from the opponent
    // profile (upper-left icon). One-time-per-match already enforced
    // engine-side via state flags; we just route the event.
    for (const ev of events) {
      if (ev.type === 'opponent-surprised') {
        this.tryFireOpponentTrigger(state, 'opponent_surprised');
      } else if (ev.type === 'opponent-gloat') {
        this.tryFireOpponentTrigger(state, 'opponent_gloat');
      }
    }

    // support: the supporting hand card (about to discard) is the
    // speaker; the bubble anchors to the recipient's ring card since
    // the supporter has no on-screen presence at this moment. Routed
    // per-event so consecutive supports in the same dispatch each
    // get their own roll.
    for (const ev of events) {
      if (ev.type !== 'support-attached') continue;
      if (Math.random() >= getConfig().voiceLineSupportProb) continue;
      this.tryFireSupportTrigger(state, ev.handInstanceId, ev.ringInstanceId);
    }

    // Voice-line log entries get pushed to state.log AFTER the main
    // render() in the standard dispatch path, so the log DOM is
    // stale. Repaint just the log section here so voice lines appear
    // alongside the engine's own log entries. Cheap (single
    // innerHTML write on a <=20-row list) and idempotent.
    refreshGameLog(this.board, state);
  }


  /** Store the rect of a played card's ring slot as a fallback voice-
   *  bubble anchor. Called when seeing a `card-played` event so that a
   *  self-destruct-on-play character (where the card never has a
   *  `.card[data-instance-id]` in the DOM after the dispatch's render())
   *  can still surface its `played` AND `die` bubbles, anchored to the
   *  stable slot element. No-op if the slot element can't be found. */
  private captureRingSlotFallbackRect(
    instanceId: string,
    side: PlayerSide,
    slotIndex: number,
  ): void {
    const slot = this.board.querySelector(
      `.ring-slot[data-side="${side}"][data-slot-index="${slotIndex}"]`,
    ) as HTMLElement | null;
    if (!slot) return;
    this.lastKnownRects.set(instanceId, slot.getBoundingClientRect());
  }

  /** Look up the character's dossier, pick a random line for the
   *  trigger, and show the bubble. Returns whether a bubble actually
   *  fired (so the combat-pair rate limiter knows whether to consume
   *  the turn slot). `bypassGate` skips the per-trigger probability
   *  roll — used by `tryFireCombatPair` which rolls once for the
   *  whole pair before calling this. */
  private tryFireCardTrigger(
    state: GameState,
    instanceId: string,
    trigger: VoiceTrigger,
    delayMs: number,
    bypassGate = false,
  ): boolean {
    if (this.charactersSpokeThisTurn.has(instanceId)) return false;
    const inst = state.cardInstances.get(instanceId);
    if (!inst) return false;
    const dossier = lookupDossierOrNull(inst.cardId);
    if (!dossier) return false;
    const lines = dossier.voice_lines[trigger];
    if (!lines || lines.length === 0) return false;
    // Played-trigger gate. Combat triggers come in via tryFireCombatPair
    // with `bypassGate=true` — their own combat-pair gate (rolled once
    // per pair) is in tryFireCombatPair using voiceLineCombatProb.
    if (!bypassGate && Math.random() >= getConfig().voiceLinePlayedProb) return false;

    // Anchor: the character's DOM element if it's still in the
    // board, otherwise a last-known rect snapshot from before the
    // most recent render() removed it. The snapshot path handles
    // non-combat deaths (action cards, ability KOs) where the card
    // was removed by render() before voice routing runs.
    const cardEl = this.board.querySelector(
      `.card[data-instance-id="${instanceId}"]`,
    ) as HTMLElement | null;
    const fallbackRect = !cardEl ? this.lastKnownRects.get(instanceId) : null;
    if (!cardEl && !fallbackRect) return false;

    const line = pickVoiceLine(lines);
    if (!line) return false;
    const def = this.catalog.get(inst.cardId);
    if (def) logVoiceLine(state.log, def.name, line, dossier.can_speak);
    showVoiceBubble({
      text: line,
      variant: dossier.can_speak ? 'speaker' : 'narrator',
      anchor: cardEl ?? fallbackRect!,
      delayMs,
    });
    this.charactersSpokeThisTurn.add(instanceId);
    return true;
  }

  /** Fire a `support` line for a hand card that's being consumed to
   *  buff a ring ally. Speaker = supporter (hand card); anchor =
   *  recipient (ring card) since the supporter has no on-screen
   *  element at this moment (just moved to discard, will be removed
   *  by the next render()). Debounce flag is set on the supporter's
   *  instanceId, which is harmless since the supporter is gone for
   *  the rest of the turn. */
  private tryFireSupportTrigger(
    state: GameState,
    supporterInstanceId: string,
    recipientInstanceId: string,
  ): void {
    if (this.charactersSpokeThisTurn.has(supporterInstanceId)) return;
    const supporterInst = state.cardInstances.get(supporterInstanceId);
    if (!supporterInst) return;
    const dossier = lookupDossierOrNull(supporterInst.cardId);
    if (!dossier) return;
    const lines = dossier.voice_lines['support'];
    if (!lines || lines.length === 0) return;
    const anchor = this.board.querySelector(
      `.card[data-instance-id="${recipientInstanceId}"]`,
    ) as HTMLElement | null;
    if (!anchor) return;
    const line = pickVoiceLine(lines);
    if (!line) return;
    const def = this.catalog.get(supporterInst.cardId);
    if (def) logVoiceLine(state.log, def.name, line, dossier.can_speak);
    showVoiceBubble({
      text: line,
      variant: dossier.can_speak ? 'speaker' : 'narrator',
      anchor,
    });
    this.charactersSpokeThisTurn.add(supporterInstanceId);
  }

  /** Fire an opponent-side bubble (surprised / gloat) from the
   *  upper-left header icon. Subject to the opponent profile having
   *  a loaded dossier; no per-character debounce (the once-per-match
   *  rule is enforced engine-side). */
  private tryFireOpponentTrigger(state: GameState, trigger: VoiceTrigger): void {
    if (!this.opponentProfileCardId) return;
    const dossier = lookupDossierOrNull(this.opponentProfileCardId);
    if (!dossier) return;
    const lines = dossier.voice_lines[trigger];
    if (!lines || lines.length === 0) return;
    const line = pickVoiceLine(lines);
    if (!line) return;
    const def = this.catalog.get(this.opponentProfileCardId);
    if (def) logVoiceLine(state.log, def.name, line, dossier.can_speak);
    showVoiceBubble({
      text: line,
      variant: dossier.can_speak ? 'speaker' : 'narrator',
      anchor: 'opponent-header',
    });
  }

  showEventPopups(events: GameEvent[]): void {
    if (this.state) this.processVoiceLineTriggers(events, this.state);
    for (const event of events) {
      if (event.type === 'power-changed') {
        const cardEl = this.board.querySelector(`.card[data-instance-id="${event.instanceId}"]`) as HTMLElement | null;
        if (cardEl) {
          const type = event.amount > 0 ? 'heal' : 'damage';
          this.showDamagePopup(cardEl, Math.abs(event.amount), type);
        }
      } else if (event.type === 'combat-damage' && event.defenderInstanceId) {
        const cardEl = this.board.querySelector(`.card[data-instance-id="${event.defenderInstanceId}"]`) as HTMLElement | null;
        if (cardEl) {
          this.showDamagePopup(cardEl, event.damage, 'damage');
        }
      } else if (event.type === 'armor-changed') {
        const cardEl = this.board.querySelector(`.card[data-instance-id="${event.instanceId}"]`) as HTMLElement | null;
        if (cardEl) {
          this.showDamagePopup(cardEl, event.amount, 'armor');
        }
      } else if (event.type === 'hp-changed') {
        const side = event.player;
        const hpContainer = this.board.querySelector(`.player-info[data-side="${side}"] .hp-bar-container`) as HTMLElement | null;
        if (hpContainer) {
          const type = event.amount > 0 ? 'heal' : 'damage';
          this.showDamagePopup(hpContainer, Math.abs(event.amount), type);
        }
      }
    }
  }

  showDamagePopup(element: HTMLElement, amount: number, type: 'damage' | 'heal' | 'spirit' | 'armor' = 'damage'): void {
    const popup = document.createElement('div');
    popup.className = `damage-popup ${type}`;
    // 'armor' / 'spirit' interpret `amount` as a signed delta (gain or
    // loss). 'spirit' gets a " SP" suffix to disambiguate from numeric
    // power/armor changes when the popup floats near multiple stats.
    // 'damage' / 'heal' take a magnitude and the sign is implied.
    if (type === 'armor') {
      popup.textContent = amount >= 0 ? `+${amount}` : `${amount}`;
    } else if (type === 'spirit') {
      popup.textContent = `${amount >= 0 ? '+' : ''}${amount} SP`;
    } else {
      popup.textContent = type === 'heal' ? `+${amount}` : `-${amount}`;
    }
    const rect = element.getBoundingClientRect();
    popup.style.left = `${rect.left + rect.width / 2}px`;
    popup.style.top = `${rect.top}px`;
    document.body.appendChild(popup);
    popup.addEventListener('animationend', () => popup.remove());
  }

  showTurnTransition(text: string): void {
    const el = document.createElement('div');
    el.className = 'turn-transition';
    el.textContent = text;
    document.body.appendChild(el);
    el.addEventListener('animationend', () => el.remove());
  }

  private checkTutorial(state: GameState): void {
    if (state.winner) { this.dismissTutorial(); return; }

    const isPlayerTurn = state.currentPlayer === 'player';
    const ps = getPlayerState(state, 'player');
    const config = getConfig();

    if (this.activeTutorial) {
      const phase = state.phase;
      let stillRelevant = false;

      if (this.activeTutorial === 'rally' && phase === 'main' && isPlayerTurn) {
        stillRelevant = ps.hand.some(handId => {
          for (let s = 0; s < ps.ring.length; s++) {
            if (canPlayCharacter(state, handId, s, this.catalog)) return true;
          }
          return false;
        });
      } else if (this.activeTutorial === 'support' && phase === 'main' && isPlayerTurn) {
        stillRelevant = ps.hand.some(handId =>
          ps.ring.some(s => s.instanceId !== null && canSupport(state, handId, s.instanceId!, this.catalog)),
        );
      } else if (this.activeTutorial === 'activate' && phase === 'main' && isPlayerTurn) {
        stillRelevant = ps.ring.some(s => s.instanceId !== null && canActivateAbility(state, s.instanceId, this.catalog));
      } else if (this.activeTutorial === 'attack' && phase === 'main' && isPlayerTurn) {
        stillRelevant = ps.ring.some(s => {
          if (!s.instanceId) return false;
          const inst = getInstance(state, s.instanceId);
          return !inst.isFresh && !inst.isExhausted;
        });
      } else if (this.activeTutorial === 'united' && phase === 'main' && isPlayerTurn) {
        stillRelevant = this.interaction.stagedAttackers.size >= 1;
      } else if (this.activeTutorial === 'endTurn' && phase === 'main' && isPlayerTurn) {
        stillRelevant = !this.hasFurtherActions(state) && this.interaction.stagedAttackers.size === 0;
      } else if (this.activeTutorial === 'pressAttack' && phase === 'main' && isPlayerTurn) {
        stillRelevant = this.interaction.stagedAttackers.size >= 1;
      } else if (this.activeTutorial === 'block' && phase === 'counter' && !isPlayerTurn) {
        stillRelevant = true;
      }

      if (stillRelevant) return;
      this.dismissTutorial();
    }

    // 1. Rally
    if (state.phase === 'main' && isPlayerTurn && !this.seenTutorials.has('rally')) {
      for (const handId of ps.hand) {
        const inst = getInstance(state, handId);
        const def = this.catalog.get(inst.cardId);
        if (def?.type === 'character') {
          for (let s = 0; s < ps.ring.length; s++) {
            if (canPlayCharacter(state, handId, s, this.catalog)) {
              this.showTutorial('rally');
              return;
            }
          }
        }
      }
    }

    // 2. Activate
    if (state.phase === 'main' && isPlayerTurn && !this.seenTutorials.has('activate')) {
      for (const slot of ps.ring) {
        if (slot.instanceId && canActivateAbility(state, slot.instanceId, this.catalog)) {
          this.showTutorial('activate');
          return;
        }
      }
    }

    // 3. Support
    if (state.phase === 'main' && isPlayerTurn && !this.seenTutorials.has('support')) {
      const canDoSupport = ps.hand.some(handId =>
        ps.ring.some(s => s.instanceId !== null && canSupport(state, handId, s.instanceId!, this.catalog)),
      );
      if (canDoSupport) {
        this.showTutorial('support');
        return;
      }
    }

    // 4. Attack — main phase with a ready attacker.
    if (state.phase === 'main' && isPlayerTurn && !this.seenTutorials.has('attack')) {
      const hasAttacker = ps.ring.some(s => {
        if (!s.instanceId) return false;
        const inst = getInstance(state, s.instanceId);
        return !inst.isFresh && !inst.isExhausted;
      });
      if (hasAttacker) {
        this.showTutorial('attack');
        return;
      }
    }

    // 5. United — main phase with one staged attacker and another eligible.
    if (state.phase === 'main' && isPlayerTurn && !this.seenTutorials.has('united')) {
      if (this.interaction.stagedAttackers.size >= 1 && ps.spirit >= config.unitedAttackCost2) {
        const moreEligible = ps.ring.some(s => {
          if (!s.instanceId) return false;
          if (this.isAnyStaged(s.instanceId)) return false;
          const inst = getInstance(state, s.instanceId);
          return !inst.isFresh && !inst.isExhausted;
        });
        if (moreEligible) {
          this.showTutorial('united');
          return;
        }
      }
    }

    // 6. Press Attack — main phase, attackers staged, nothing else to do.
    //    Checked before endTurn because the staging condition is more specific.
    if (state.phase === 'main' && isPlayerTurn && !this.seenTutorials.has('pressAttack')) {
      if (this.interaction.stagedAttackers.size >= 1 && !this.hasFurtherActions(state)) {
        this.showTutorial('pressAttack');
        return;
      }
    }

    // 7. End Turn — main phase, no staged attackers, nothing else to do.
    if (state.phase === 'main' && isPlayerTurn && !this.seenTutorials.has('endTurn')) {
      if (this.interaction.stagedAttackers.size === 0 && !this.hasFurtherActions(state)) {
        this.showTutorial('endTurn');
        return;
      }
    }

    // 8. Block
    if (state.phase === 'counter' && !isPlayerTurn && !this.seenTutorials.has('block')) {
      const hasBlocker = ps.ring.some(s => {
        if (!s.instanceId) return false;
        const inst = getInstance(state, s.instanceId);
        return !inst.isExhausted;
      });
      if (hasBlocker && state.combatState) {
        this.showTutorial('block');
        return;
      }
    }

    this.dismissTutorial();
  }

  /** True if the player has any move available this main phase besides
   *  pressing the main action button: a hand play, support, activate, an
   *  unstaged eligible attacker to add, or a forced discard. Used by the
   *  endTurn / pressAttack tutorials to detect "nothing left to do". */
  private hasFurtherActions(state: GameState): boolean {
    const ps = getPlayerState(state, 'player');
    for (const handId of ps.hand) {
      if (canPlayAction(state, handId, this.catalog)) return true;
      for (let s = 0; s < ps.ring.length; s++) {
        if (canPlayCharacter(state, handId, s, this.catalog)) return true;
      }
      for (const slot of ps.ring) {
        if (slot.instanceId && canSupport(state, handId, slot.instanceId, this.catalog)) return true;
      }
    }
    for (const slot of ps.ring) {
      if (!slot.instanceId) continue;
      if (canActivateAbility(state, slot.instanceId, this.catalog)) return true;
      if (this.isAnyStaged(slot.instanceId)) continue;
      const inst = getInstance(state, slot.instanceId);
      if (!inst.isFresh && !inst.isExhausted) return true;
    }
    if (ps.hand.length > getMaxHandSize(state, 'player', this.catalog)) return true;
    return false;
  }

  private showTutorial(key: TutorialKey): void {
    if (this.activeTutorial === key) return;
    this.dismissTutorial();

    this.activeTutorial = key;
    saveTutorialSeen(key, this.seenTutorials);

    this.tutorialEl = document.createElement('div');
    this.tutorialEl.className = 'tutorial-banner';
    this.tutorialEl.innerHTML = `
      <div class="tutorial-text">${TUTORIAL_MESSAGES[key]}</div>
      <button class="tutorial-dismiss">Got it</button>
    `;
    this.tutorialEl.querySelector('.tutorial-dismiss')!.addEventListener('click', () => {
      this.dismissTutorial();
    });
    this.board.appendChild(this.tutorialEl);
  }

  private dismissTutorial(): void {
    if (this.tutorialEl) {
      this.tutorialEl.remove();
      this.tutorialEl = null;
    }
    this.activeTutorial = null;
  }

  private showGameOver(state: GameState): void {
    const overlay = document.createElement('div');
    overlay.className = 'game-over-overlay';

    const isWin = state.winner === 'player';
    const reason = state.winReason === 'hp' ? 'HP reduced to 0' : 'Deck out';
    const coins = isWin ? this.rewardPreview.winCoins : this.rewardPreview.lossCoins;
    const gems = isWin ? this.rewardPreview.winGems : this.rewardPreview.lossGems;
    // Tickets ride alongside coins/gems on the rewards screen rather
    // than being teased on the pre-battle modal — the user wanted the
    // FREE-pack drop to feel like a reveal, not a promised quest goal.
    const tickets = isWin ? this.rewardPreview.winTickets : [];
    const ticketLines = tickets
      .filter(t => t.count > 0)
      .map(t => {
        const name = ticketDisplayName(t.id);
        const label = t.count > 1 ? `${t.count}× ${name}` : name;
        return `<p class="tickets-earned">${escapeHtml(label)}</p>`;
      })
      .join('');
    // Show "Claim Pack" only when the player actually has somewhere
    // useful to go: they won, they earned a ticket, and there's no
    // card reward incoming. If a card is also coming, the upcoming
    // pack-opening view will offer "Add to Deck" instead — chaining
    // both buttons would crowd the player and the user's preference
    // is to prioritize Add to Deck.
    const showClaimPack = isWin
      && tickets.some(t => t.count > 0)
      && !this.rewardPreview.winHasCardReward;
    const claimPackBtnHtml = showClaimPack
      ? '<button class="action-button secondary" id="claim-pack-btn">Claim Pack</button>'
      : '';

    // Tournament rank line — always shows the *current* rank, even
    // when this match didn't update priorities (because other players
    // may have moved). The optional "match not recorded" note
    // explains *why* this match in particular didn't influence the
    // rank, without claiming the rank itself is frozen.
    const rankBlock = renderTournamentRankBlock(this.rewardPreview.tournamentRankChange);

    overlay.innerHTML = `
      <div class="game-over-panel">
        <h1 class="${isWin ? 'win' : 'lose'}">${isWin ? 'Victory!' : 'Defeat'}</h1>
        <p class="subtitle">${reason}</p>
        ${coins > 0 ? `<p class="coins-earned">+${coins} coins</p>` : ''}
        ${gems > 0 ? `<p class="gems-earned">+${gems} gems</p>` : ''}
        ${ticketLines}
        ${rankBlock}
        <div class="game-over-buttons">
          ${claimPackBtnHtml}
          <button class="action-button" id="restart-btn">Continue</button>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);
    // Elevate the game log above the overlay scrim while the modal is
    // up so the player can still scroll back through what just
    // happened (the overlay's blur + scrim would otherwise bury it).
    // Scoped to a body class so the log keeps its normal z-order
    // during play. Removed on dismiss.
    document.body.classList.add('game-over-active');
    const dismiss = (action: GameOverAction): void => {
      overlay.remove();
      document.body.classList.remove('game-over-active');
      if (this.onGameOverCallback && state.winner) {
        this.onGameOverCallback(state.winner, action);
      }
    };
    overlay.querySelector('#restart-btn')!.addEventListener('click', () => dismiss('continue'));
    overlay.querySelector('#claim-pack-btn')?.addEventListener('click', () => dismiss('claim-pack'));
  }
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Walk the combat-damage events of a resolved combat and enumerate
 *  every (character, trigger) tuple that could speak at resolution
 *  time. Eligibility per the design spec:
 *    Attacker (initiated the attack): die (if they die) OR kill_other
 *      (if the defender dies and they don't). NOT combat_engage — that
 *      fired at staging time, before blockers were assigned.
 *    Defender (blocker): die OR kill_other OR combat_engage (if
 *      neither dies — the defender's "I'm fighting you" line).
 *
 *  Pair identification: scan combat-damage events whose attacker is
 *  on the attacking side (`state.currentPlayer`). That filter skips
 *  blocker→attacker counter-damage events in individual combat AND
 *  the blocker→each-attacker events in united combat — both go the
 *  "wrong way" for our notion of (attacker, defender). Each filtered
 *  event represents one attack pair.
 *
 *  Unblocked attacks (defenderInstanceId is null) emit a single
 *  attacker-side event with no character defender — only the
 *  attacker is eligible there (die / kill_other, neither of which
 *  fires for a successful unblocked attack since the attacker
 *  doesn't take counter damage and the "defender" is the opposing
 *  player's HP, not a character that dies in our `killed` set). */
function collectCombatResolutionEligibles(
  state: GameState,
  events: GameEvent[],
): Array<{ instanceId: string; trigger: VoiceTrigger }> {
  const killed = new Set<string>();
  for (const ev of events) {
    if (ev.type === 'kill-credit') killed.add(ev.victimInstanceId);
  }

  const eligibles: Array<{ instanceId: string; trigger: VoiceTrigger }> = [];
  const seenPairs = new Set<string>();           // dedupe identical (a, d) entries
  for (const ev of events) {
    if (ev.type !== 'combat-damage') continue;
    const attackerInst = state.cardInstances.get(ev.attackerInstanceId);
    if (!attackerInst) continue;
    // Skip counter-damage events — only consider events going from
    // the attacking side toward the defender.
    if (attackerInst.owner !== state.currentPlayer) continue;
    const pairKey = `${ev.attackerInstanceId}->${ev.defenderInstanceId ?? '∅'}`;
    if (seenPairs.has(pairKey)) continue;
    seenPairs.add(pairKey);

    const attackerId = ev.attackerInstanceId;
    const defenderId = ev.defenderInstanceId;
    const attackerDies = killed.has(attackerId);
    const defenderDies = defenderId ? killed.has(defenderId) : false;

    // Attacker eligibility: die or kill_other (no combat_engage here).
    if (attackerDies) {
      eligibles.push({ instanceId: attackerId, trigger: 'die' });
    } else if (defenderDies) {
      eligibles.push({ instanceId: attackerId, trigger: 'kill_other' });
    }

    // Defender eligibility (blocked only): full priority list.
    if (defenderId) {
      if (defenderDies) {
        eligibles.push({ instanceId: defenderId, trigger: 'die' });
      } else if (attackerDies) {
        eligibles.push({ instanceId: defenderId, trigger: 'kill_other' });
      } else {
        eligibles.push({ instanceId: defenderId, trigger: 'combat_engage' });
      }
    }
  }
  return eligibles;
}

function renderTournamentRankBlock(change: TournamentRankChange | null | undefined): string {
  if (!change) return '';
  const rankLine = change.oldRank !== change.newRank
    ? `<p class="tournament-rank">Rank: <strong>#${change.oldRank}</strong> → <strong>#${change.newRank}</strong></p>`
    : `<p class="tournament-rank">Rank: <strong>#${change.newRank}</strong></p>`;
  const note = !change.rankUpdated && change.tournamentEnded
    ? `<p class="tournament-rank-note">Match not recorded — the tournament has ended.</p>`
    : '';
  return rankLine + note;
}
